import {
  IconButton,
  Link,
  Stack,
  Typography,
  Chip,
  Button,
  Menu,
  MenuItem,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";
import {
  CustomDetailsList,
  Loader,
  LoadingButton,
  PageListLayout,
  usePermission,
} from "../../../shared";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useSubmissionsDetails } from "../hooks";
import { MoveToDraftDialog } from "../components";
import { arraySort, formatCost } from "../../../utils";

export const SubmissionsDetails = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("/store-admin");
  const canStockTakeModify = usePermission("stock-take-modify");

  const {
    submission,
    itemsByCategoryAndSubcategory,
    statusLabel,
    statusColor,
    showMoveToDraft,
    openMoveToDraftDialog,
    handleMoveToDraftClick,
    handleMoveToDraftCancel,
    handleMoveToDraftConfirm,
    isMovingToDraft,
    isLoading,
    anchorEl,
    openExportMenu,
    handleExportClick,
    handleExportClose,
    handlePrint,
    handleExportPDF,
    handleExportExcel,
  } = useSubmissionsDetails();

  const breadcrumbs = [
    <Link
      underline="none"
      key="1"
      color="primary.main"
      onClick={() => navigate("/submissions")}
      sx={{ fontSize: "14px", fontWeight: 400, cursor: "pointer" }}
    >
      Stock Submissions
    </Link>,
    <Typography
      key="2"
      color="inherit"
      sx={{ fontSize: "14px", fontWeight: 400 }}
    >
      {submission?.submission_date}
    </Typography>,
  ];

  const columns = [
    {
      title: "Date of Submission",
      accessor: (val) => (
        <Typography variant="subtitle2" fontWeight={500}>
          {val?.submission_date || "--"}
        </Typography>
      ),
    },
    ...(!isStoreAdmin
      ? [
          {
            title: "Store Name",
            accessor: (val) => (
              <Typography variant="subtitle2" fontWeight={500}>
                {val?.store_name || "--"}
              </Typography>
            ),
          },
        ]
      : []),
    {
      title: "Submitted By",
      accessor: (val) => (
        <Typography variant="subtitle2" fontWeight={500}>
          {val?.submitted_by_name || "--"}
        </Typography>
      ),
    },
    {
      title: "Entered By",
      accessor: (val) => (
        <Typography variant="subtitle2" fontWeight={500}>
          {val?.entered_by || "--"}
        </Typography>
      ),
    },
    {
      title: "Remarks",
      accessor: (val) => (
        <Typography variant="subtitle2" fontWeight={500}>
          {val?.remarks || "--"}
        </Typography>
      ),
    },
  ];

  // Tag card colors — first is reserved for Stock Amount
  const TAG_COLORS = [
    "#A04D3D",
    "#564845",
    "#7B5E57",
    "#6B8E6F",
    "#8B6F66",
    "#4A6741",
    "#7A5C4E",
  ];

  const summaryCardsData = [
    // First card: total stock amount — always #5E7360
    {
      label: "Stock Amount",
      total: `$${submission?.total_stock_amount ?? 0}`,
      color: "#5E7360",
    },
    // Remaining cards: one per tag from tag_wise_totals
    ...(submission?.tag_wise_totals ?? []).map((tag, index) => ({
      label: tag.tag_name,
      total: `$${tag.total_amount.toFixed(2)}`,
      color: TAG_COLORS[index % TAG_COLORS.length],
    })),
  ];

  return (
    <>
      <PageListLayout
        breadcrumbs={breadcrumbs}
        headerText={
          <Stack alignItems="center" direction="row" gap="12px">
            <Typography variant="h6" fontWeight={600} fontSize="24px">
              {submission?.submission_date}
            </Typography>
            {statusLabel && (
              <Chip
                size="small"
                color={statusColor}
                variant={submission?.status === 2 ? "draft" : "filled"}
                label={statusLabel.toUpperCase()}
              />
            )}
          </Stack>
        }
        rightContent={
          <Stack direction="row" alignSelf="center" gap={1}>
            <IconButton onClick={handlePrint}>
              <img alt="print" src="/icons/ic_print.png" />
            </IconButton>
            {showMoveToDraft && canStockTakeModify ? (
              <LoadingButton
                variant="outlined"
                color="gray2"
                onClick={handleMoveToDraftClick}
                loading={isMovingToDraft}
                sx={{ textTransform: "none" }}
              >
                Move to Draft
              </LoadingButton>
            ) : null}
            <Button
              variant="outlined"
              color="gray2"
              onClick={handleExportClick}
              endIcon={<ExpandMoreIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#fff !important",
              }}
            >
              Export
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={openExportMenu}
              onClose={handleExportClose}
              sx={{
                "& .MuiMenu-paper": {
                  minWidth: "180px",
                  boxShadow: "none",
                  border: "1px solid #E6EEF7",
                },
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
              <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
            </Menu>
          </Stack>
        }
        sx={{
          p: "16px",
          pt: 0,
          backgroundColor: "background.paper",
        }}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {/* Summary Section */}
            <Box sx={{ mb: 2, mt: 3 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize="18px"
                sx={{ mb: 2 }}
              >
                Summary
              </Typography>
              <Stack
                sx={{
                  flexDirection: "row",
                  rowGap: "16px",
                  columnGap: "16px",
                  flexWrap: "wrap",
                }}
              >
                {summaryCardsData.map((card, index) => (
                  <Stack
                    key={index}
                    sx={{
                      justifyContent: "center",
                      alignItems: "flex-start",
                      backgroundColor: card.color,
                      borderRadius: "6px",
                      padding: "12px 16px",
                      maxWidth: "180px",
                      height: "63px",
                      flex: {
                        xs: "1 1 calc(50% - 8px)",
                        sm: "1 1 calc(33.333% - 11px)",
                        md: "0 1 180px",
                      },
                      color: "#fff",
                    }}
                  >
                    <Typography fontSize="12px" fontWeight={500}>
                      {card.label}
                    </Typography>
                    <Typography fontWeight={600} fontSize="20px">
                      {card.total}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Basic Details Section */}
            <CustomDetailsList
              columns={columns}
              data={submission}
              title="Basic Details"
            />

            {/* Stock submissions section */}
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize="18px"
                sx={{ mb: 2 }}
              >
                Stock Submissions
              </Typography>
              <Divider />
              {itemsByCategoryAndSubcategory.map(
                ({ id: categoryId, name: categoryName, subcategories }) => {
                  const categoryTotal = subcategories
                    .flatMap(({ items }) => items)
                    .reduce((sum, item) => sum + Number(item.amount), 0.0);

                  return (
                    <Stack
                      key={categoryId}
                      sx={{
                        backgroundColor: "background.default",
                        padding: "8px",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        fontSize="18px"
                        sx={{ mb: 1 }}
                      >
                        {categoryName}
                      </Typography>
                      <TableContainer
                        component={Paper}
                        sx={{
                          boxShadow: "none",
                          border: "1px solid #DFE8F2",
                          borderRadius: 0,
                        }}
                      >
                        <Table
                          sx={{
                            "& .MuiTableCell-root": {
                              borderRight: "1px solid #DFE8F2",
                            },
                            "& .MuiTableCell-root:last-child": {
                              borderRight: "none",
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#D6DDD3" }}>
                              <TableCell
                                component="th"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                }}
                              >
                                Product
                              </TableCell>
                              <TableCell
                                component="th"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  width: "127px",
                                }}
                              >
                                Stock Code
                              </TableCell>
                              <TableCell
                                component="th"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  width: "127px",
                                }}
                              >
                                Stock Unit
                              </TableCell>
                              <TableCell
                                component="th"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  width: "127px",
                                }}
                              >
                                Reference Unit
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  width: "127px",
                                }}
                              >
                                Cost(Ex GST)/Unit
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  width: "127px",
                                }}
                              >
                                Stock On Hand
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  width: "127px",
                                }}
                              >
                                Amount
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {subcategories.map(
                              ({
                                id: subcategoryId,
                                name: subcategoryName,
                                items,
                              }) => {
                                const subtotal = items.reduce(
                                  (sum, item) => sum + Number(item.amount ?? 0),
                                  0,
                                );
                                return (
                                  <React.Fragment key={subcategoryId}>
                                    {/* Subcategory header row */}
                                    <TableRow
                                      sx={{ backgroundColor: "neutral.98" }}
                                    >
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "13px",
                                          py: "6px",
                                        }}
                                      >
                                        {subcategoryName}
                                      </TableCell>
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                      <TableCell />
                                    </TableRow>
                                    {/* Item rows */}
                                    {arraySort(items, "stock_item_name").map(
                                      (row) => (
                                        <TableRow key={row.id}>
                                          <TableCell>
                                            {row.stock_item_name}
                                          </TableCell>
                                          <TableCell align="left">
                                            {row.stock_code || "--"}
                                          </TableCell>
                                          <TableCell align="left">
                                            {row.unit_name}
                                          </TableCell>
                                          <TableCell align="left">
                                            {row?.stock_alt_unit
                                              ? row.stock_alt_unit
                                              : ""}
                                          </TableCell>
                                          <TableCell align="left">
                                            ${formatCost(row.unit_cost)}
                                          </TableCell>
                                          <TableCell align="left">
                                            {row.stock_on_hand}
                                          </TableCell>
                                          <TableCell align="left">
                                            ${row.amount}
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )}
                                    {/* Subtotal row */}
                                    <TableRow
                                      sx={{ backgroundColor: "neutral.90" }}
                                    >
                                      <TableCell colSpan={5} />
                                      <TableCell
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "13px",
                                        }}
                                      >
                                        SUB TOTAL
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "13px",
                                        }}
                                        align="left"
                                      >
                                        ${Number(subtotal).toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              },
                            )}
                            {/* Category total row */}
                            <TableRow sx={{ backgroundColor: "neutral.80" }}>
                              <TableCell colSpan={5} />
                              <TableCell
                                sx={{ fontWeight: 700, fontSize: "14px" }}
                              >
                                TOTAL
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 700, fontSize: "14px" }}
                              >
                                ${Number(categoryTotal).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  );
                },
              )}
            </Box>
          </>
        )}
      </PageListLayout>

      <MoveToDraftDialog
        open={openMoveToDraftDialog}
        onConfirm={handleMoveToDraftConfirm}
        onCancel={handleMoveToDraftCancel}
      />
    </>
  );
};
